import {
  Alert,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
} from "antd";
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from 'moment';
import 'moment/locale/es';
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import codes from '../../../utils/providerCodes.json';
import ApplicantModal from './ApplicantModal';
import ChildCompanyModal from './ChildCompanyModal';
import CostCenterModal from './CostCenterModal';
import BillingBlock from './GeneralDataPE/BillingBlock';
import { useGeneralDataPE } from '../hooks/useGeneralDataPE';

const { Option } = Select;

const GeneralDataPE = ({
  type,
  form,
  reservation,
  setReservation,
  setTotalPatients,
  disablePatientInput,
  setRefreshForm,
  setBranchOfficeId,
  sessionBranchOfficeId,
  fetchCurrency,
  paymentData,
  companies,
  setCompanies,
  countrySettings,
  countryCode = "PE",
  paymentAmount,
  isLoadingPaymentAmount,
}) => {
  const { t } = useTranslation();
  const {
    loading,
    applicantLoading,
    childCompanyLoading,
    applicantList,
    setApplicantList,
    isDebtor,
    branchOffices,
    applicantModal,
    setApplicantModal,
    childCompanyModal,
    setChildCompanyModal,
    costCenterModal,
    setCostCenterModal,
    costsCenters,
    setCostsCenters,
    childCompanies,
    setChildCompanies,
    showChildCompanies,
    setShowChildCompanies,
    addApplicant,
    addCompany,
    addChildCompany,
    addCostCenter,
    fetchApplicants,
    fetchBranchOffices,
    searchChildCompanies,
    handleOnChangeSearch,
    invoiceCompanyOptions,
    initializeInvoiceOptions,
  } = useGeneralDataPE({ form, companies, setCompanies });

  const disabledDate = (current) => {
    return current && current < moment().endOf('day').subtract(1, 'day');
  }

  const handlePatientsQuantity = (value) => {
    if (value) {
      setTotalPatients(parseInt(value));
    }
  };

  useEffect(() => {
    if (reservation && !type) {

      setApplicantList([{
        "id": reservation.user_id,
        "fullname": reservation.user_fullname
      }]);

      if (reservation.cost_center.id) {
        setCostsCenters([{
          "id": reservation.cost_center.id,
          "code": reservation.cost_center.code,
          "name": reservation.cost_center.name,
        }]);
      }

      if (reservation.child_company_id) {
        setChildCompanies([{
          "id": reservation.child_company.id,
          "business_name": reservation.child_company.business_name
        }]);
        setShowChildCompanies(true);
      }

      setCompanies([
        {
          business_name: reservation.company.business_name,
          id: reservation.company_id,
          isComplete: reservation.company.isComplete,
        },
        (reservation.outsourcing && {
          business_name: reservation.outsourcing.business_name,
          id: reservation.outsourcing_id,
          isComplete: reservation.outsourcing.isComplete,
        }),
        (reservation.third_company && {
          business_name: reservation.third_company.business_name,
          id: reservation.third_company_id,
          isComplete: reservation.third_company.isComplete,
        }),
        (reservation.invoice_company && {
          business_name: reservation.invoice_company.business_name,
          id: reservation.invoice_company_id,
          isComplete: reservation.invoice_company.isComplete,
        }),
      ]);

      initializeInvoiceOptions(reservation);

      form.setFieldsValue({
        'id': reservation.id,
        'state': reservation.state,
        'user_id': reservation.user_id,
        'user_name': reservation.user_name,
        'company_id': reservation.company_id,
        'child_company_id': reservation.child_company_id && reservation.child_company_id,
        'cost_center_id': reservation.cost_center.id && reservation.cost_center.id,
        'observations': reservation.observations,
        'contact_method': reservation.contact_method,
        'branch_office_id': reservation.branch_office_id,
        'reservation_date': moment(reservation.reservation_date, 'YYYY-MM-DD'),
        'total_patients': parseInt(reservation.total_patients),
        ...(countrySettings.generalData.outsourcing[countryCode] && { 'outsourcing_id': reservation.outsourcing_id }),
        ...(countrySettings.generalData.thirdCompany[countryCode] && { 'third_company_id': reservation.third_company_id }),
        ...(countrySettings.generalData.invoiceTo[countryCode] && { 'invoice_company_id': reservation.invoice_company_id }),
      });

      setReservation(reservation);
    }
  }, [
    form,
    reservation,
    type,
    countryCode,
    countrySettings.generalData.invoiceTo,
    countrySettings.generalData.outsourcing,
    countrySettings.generalData.thirdCompany,
    setCompanies,
    setReservation,
    setApplicantList,
    setChildCompanies,
    setCostsCenters,
    setShowChildCompanies,
    initializeInvoiceOptions,
  ]);

  useEffect(() => {
    fetchBranchOffices();
    fetchCurrency();
  }, [fetchCurrency, fetchBranchOffices]);

  return (
    <Row justify="center" className="reservation-settings-wrapper__general-data pt-40 pb-20">
      <Col lg={22} xs={22}>
        <Row>
          <h2>{t('reservations.generalDataForm.title')}</h2>
        </Row>
        <Row gutter={[0, 15]}>
          <Col lg={24} xs={24}>
            <ApplicantModal
              reservationForm={form}
              addApplicant={addApplicant}
              applicantModal={applicantModal}
              setApplicantModal={setApplicantModal}
            />
            <ChildCompanyModal
              reservationForm={form}
              companyId={form.getFieldValue('company_id')}
              addChildCompany={addChildCompany}
              childCompanyModal={childCompanyModal}
              setChildCompanyModal={setChildCompanyModal}
            />
            <CostCenterModal
              reservationForm={form}
              companyId={form.getFieldValue('company_id')}
              addCostCenter={addCostCenter}
              costCenterModal={costCenterModal}
              setCostCenterModal={setCostCenterModal}
            />
            <Form form={form} size="large" layout="vertical">
              <Row gutter={15}>
                <Col lg={8} xs={24}>
                  <Form.Item name="state" className="d-none">
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item name="id" className="d-none">
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item name="branch_office" className="d-none">
                    <Input type="hidden" />
                  </Form.Item>

                  <Spin className="cmt-spin" spinning={applicantLoading}>
                    <Row>
                      <Col lg={24} xs={24}>
                        <Form.Item
                          name="user_id"
                          label={t('reservations.generalDataForm.applicantLabel')}
                          className="mb-10"
                          rules={[{
                            required: true,
                            message: 'Solicitante es requerido'
                          }]}
                        >
                          <Select
                            className="cmt-select"
                            placeholder={t('reservations.generalDataForm.applicantPlaceholder')}
                            showSearch
                            showArrow={false}
                            filterOption={false}
                            allowClear
                            onSearch={fetchApplicants}
                            onChange={
                              (value, option) => handleOnChangeSearch('applicant', value, option, setApplicantList)
                            }
                            onClear={(value, option) => handleOnChangeSearch('applicant', value, option, setApplicantList)}
                            notFoundContent={
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                  <div className="empty-search">
                                    <div>No hay coincidencias</div>
                                  </div>
                                }
                              />
                            }
                          >
                            {
                              applicantList &&
                              applicantList.map(option => {
                                return (
                                  <Option key={option.id} value={option.id} >
                                    {option.fullname}
                                  </Option>
                                )
                              })
                            }
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Spin>
                </Col>

                <BillingBlock
                  form={form}
                  companies={companies}
                  setCompanies={setCompanies}
                  childCompanies={childCompanies}
                  setChildCompanies={setChildCompanies}
                  costsCenters={costsCenters}
                  setCostsCenters={setCostsCenters}
                  showChildCompanies={showChildCompanies}
                  childCompanyLoading={childCompanyLoading}
                  setChildCompanyModal={setChildCompanyModal}
                  handleOnChangeSearch={handleOnChangeSearch}
                  addCompany={addCompany}
                  searchChildCompanies={searchChildCompanies}
                  sessionBranchOfficeId={sessionBranchOfficeId}
                  countrySettings={countrySettings}
                  countryCode={countryCode}
                  invoiceCompanyOptions={invoiceCompanyOptions}
                />
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="contact_method"
                    label={t('reservations.generalDataForm.contactMethodLabel')}
                    className="general-data contact-method select-wrapper mb-10"
                    rules={[{ required: true, message: t('reservations.generalDataForm.errorContact') }]}
                  >
                    <Select
                      className="cmt-select"
                      placeholder={t('reservations.generalDataForm.contactMethodPlaceholder')}
                    >
                      <Option value={"way_web"}>Web</Option>
                      <Option value={"way_phone"}>Teléfono</Option>
                      <Option value={"way_email"}>Correo electrónico</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24} className="general-data branch-office select-wrapper">
                  <Form.Item
                    name="branch_office_id"
                    label={t('reservations.generalDataForm.branchOfficeLabel')}
                    className="mb-10"
                    rules={[{ required: true, message: 'Sede es requerida' }]}
                  >
                    {
                      loading ?
                        <Row justify="center">
                          <Spin className="cmt-spin" />
                        </Row>
                        :
                        <Select
                          showSearch
                          className="cmt-select"
                          placeholder={t('reservations.generalDataForm.branchOfficePlaceholder')}
                          onChange={(value) => {
                            setRefreshForm(new Date().getUTCMilliseconds());
                            setBranchOfficeId(value)
                          }}
                          filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                        >
                          {
                            branchOffices && branchOffices.map((office, index) => {
                              return (
                                <Option key={index} value={office.id}>
                                  {office.name}
                                </Option>
                              )
                            })}
                        </Select>
                    }
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="reservation_date"
                    label={t('reservations.generalDataForm.dateLabel')}
                    className="mb-10"
                    help="Debe ingresar una fecha mayor a la de hoy"
                    rules={[
                      {
                        required: true,
                        message: `Fecha de ${t('reservations.generalDataForm.reservation').toLocaleLowerCase()} es requerida`
                      },
                      {
                        validator: async (_, endDatetime) => {
                          const current = endDatetime;
                          if (current && current < moment().endOf('day').subtract(1, 'day')) {
                            return Promise.reject(
                              "Debe ingresar una fecha mayor a la de hoy"
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      onChange={() => setRefreshForm(new Date().getUTCMilliseconds())}
                      locale={locale}
                      placeholder={t('reservations.generalDataForm.datePlaceholder')}
                      popupClassName="custom-dropdown"
                      className="cmt-date-picker w-100"
                      disabledDate={disabledDate}
                      format="DD-MM-YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item
                    name="total_patients"
                    label={t('reservations.generalDataForm.totalPatientsLabel')}
                    className="mb-10"
                    rules={[
                      {
                        required: true,
                        message: 'Total de trabajadores es requerido',
                        type: 'integer',
                      },
                    ]}
                  >
                    <InputNumber
                      onChange={handlePatientsQuantity}
                      placeholder={t('reservations.generalDataForm.totalPatientsPlaceholder')}
                      min={1}
                      max={100}
                      className="cmt-input-number w-100"
                      disabled={disablePatientInput}
                    />
                  </Form.Item>
                </Col>
                <Col lg={24} xs={24}>
                  <Form.Item
                    name="observations"
                    label="Observaciones"
                    className="mb-10"
                  >
                    <Input.TextArea
                      className="cmt-input"
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      placeholder="Ingrese observaciones de la cita"
                    />
                  </Form.Item>
                </Col>
              </Row>
              {isDebtor && <Row>
                <Col className="mt-20 mb-20">
                  <Alert
                    className="cmt-alert"
                    message={
                      <div dangerouslySetInnerHTML={{ __html: t('reservations.generalDataForm.blockedClient') }} />
                    }
                    type="info"
                    showIcon />
                </Col>
              </Row>}
              <Row gutter={15} className="mt-20">
                <Col lg={8} xs={24}>
                  <p className="mb-0">
                    Método de pago:
                    <strong className="ml-5">
                      {
                        paymentData && paymentData.length > 0 && reservation.payment_methods.length > 0 ?
                          (
                            paymentData.find(payment => payment.id === reservation.payment_methods[0])
                            || { name: 'No encontrado' }
                          ).name
                          :
                          'No seleccionado'
                      }
                    </strong>
                  </p>
                </Col>
                {
                  reservation.payment_methods_codes && reservation.payment_methods_codes.length > 0 &&
                  (reservation.payment_methods_codes.includes(codes.mercadopagocl) || reservation.payment_methods_codes.includes(codes.toku)) &&
                  <>
                    <Col lg={8} xs={24}>
                        <p className="mb-0">
                          Estado de pago:
                          <strong className="ml-5">{reservation.paid ? 'Pagado' : 'No pagado'}</strong>
                        </p>
                    </Col>
                    <Col lg={8} xs={24}>
                      <p className="mb-0">
                        Monto pagado:
                        {
                          isLoadingPaymentAmount ? (
                            <p className="payment-amount-spinner mb-0">
                              <Spin size="small" className="cmt-spin" />
                            </p>
                          ) : (
                            <strong className="ml-5">
                              {paymentAmount?.total
                                ? `$${paymentAmount.total.toLocaleString('es-CL')}`
                                : '$0'}
                            </strong>
                          )
                        }
                      </p>
                    </Col>
                  </>
                }
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default GeneralDataPE;
